import type {
  AdminEventRegistration,
  AdminEventRegistrationListResponse,
  AdminEventRegistrationResponse,
  CancelAllEventRegistrationsRequest,
  CancelAllEventRegistrationsResponse,
  CancelEventRegistrationRequest,
  CancelEventRegistrationResponse,
  CreateAdminEventRegistrationRequest,
  CreateAdminEventRegistrationResponse,
  EventCheckinRequest,
  EventCheckinResponse,
  PromoteEventRegistrationResponse,
  RecordEventRegistrationPaymentRequest,
  RecordEventRegistrationPaymentResponse,
} from '@mds/shared/types/event-registration';

import { api } from '@/lib/api';

/** Registrations, tickets and door check-in — see `backend/docs/event-registrations.md`. */

/** A type alias, so it carries the index signature the client's `query` needs. */
export type RegistrationQuery = {
  page?: number;
  limit?: number;
  /** Name, email, phone, or an `MDS-…` reference. */
  q?: string;
  status?: 'all' | 'waitlisted' | 'payment_pending' | 'confirmed' | 'cancelled';
  attendance?: 'all' | 'pending' | 'checked_in';
  /** `waitlistedAt` ascending is the waitlist in the order parties joined it. */
  sort?: 'createdAt' | 'waitlistedAt' | 'attendeeName' | 'reference';
  direction?: 'asc' | 'desc';
};

const base = (eventId: string) => `admin/events/${eventId}/registrations`;

export const listRegistrations = (eventId: string, query: RegistrationQuery = {}) =>
  api.get<AdminEventRegistrationListResponse>(base(eventId), { query });

export const getRegistration = (
  eventId: string,
  registrationId: string,
): Promise<AdminEventRegistration> =>
  api.get<AdminEventRegistrationResponse>(`${base(eventId)}/${registrationId}`).then((r) => r.data);

/**
 * A free event confirms at once and issues the ticket; a priced one waits in
 * `payment_pending`, holding no seats, until full payment is recorded.
 */
export const createRegistration = (eventId: string, body: CreateAdminEventRegistrationRequest) =>
  api.post<CreateAdminEventRegistrationResponse>(base(eventId), body);

/**
 * Takes a party off the waitlist. A free one is confirmed and ticketed, which
 * needs a free seat; a priced one moves to payment due without a capacity check.
 */
export const promoteRegistration = (eventId: string, registrationId: string) =>
  api.post<PromoteEventRegistrationResponse>(`${base(eventId)}/${registrationId}/promote`);

/** Moves an unpaid party to the waitlist, so it can no longer be confirmed by paying. */
export const waitlistRegistration = (
  eventId: string,
  registrationId: string,
): Promise<AdminEventRegistration> =>
  api
    .post<AdminEventRegistrationResponse>(`${base(eventId)}/${registrationId}/waitlist`)
    .then((r) => r.data);

/** Full payment only. There is no amount: the server records the snapshotted total. */
export const recordPayment = (
  eventId: string,
  registrationId: string,
  body: RecordEventRegistrationPaymentRequest,
) =>
  api.post<RecordEventRegistrationPaymentResponse>(
    `${base(eventId)}/${registrationId}/payments`,
    body,
  );

/** Releases the seats and revokes the ticket. Any payment stays, non-refundable. */
export const cancelRegistration = (
  eventId: string,
  registrationId: string,
  body: CancelEventRegistrationRequest,
) => api.post<CancelEventRegistrationResponse>(`${base(eventId)}/${registrationId}/cancel`, body);

/**
 * Cancels every registration on the event as `cancelRegistration` would, each
 * emailed, with the one reason. Checked-in parties are left as they are.
 */
export const cancelAllRegistrations = (eventId: string, body: CancelAllEventRegistrationsRequest) =>
  api
    .post<CancelAllEventRegistrationsResponse>(`${base(eventId)}/cancel-all`, body)
    .then((r) => r.data);

/**
 * Exactly one identifier. Handled outcomes — valid, already, invalid — all come
 * back as 200, so a refused ticket is data to show, not an error to catch.
 */
export const checkIn = (eventId: string, body: EventCheckinRequest) =>
  api.post<EventCheckinResponse>(`admin/events/${eventId}/check-in`, body);

/** The attendance checkbox: sets or corrects it, writing a history row on change. */
export const setAttendance = (eventId: string, registrationId: string, checkedIn: boolean) =>
  api.put<EventCheckinResponse>(`${base(eventId)}/${registrationId}/attendance`, { checkedIn });
